import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Toaster } from '@/components/ui/toaster';
import Header from '@/components/Header';
import IdleSessionManager from '@/components/IdleSessionManager';
import Footer from '@/components/Footer';
import Home from '@/pages/Home'; // page principale: chargée directement
import ProtectedRoute from '@/components/ProtectedRoute';
import RequireAdmin from '@/components/RequireAdmin';
import RequireDesignerOrAdmin from '@/components/RequireDesignerOrAdmin';
import { Suspense, lazy } from 'react';
import BottomActionButton from '@/components/BottomActionButton';

// Code splitting (lazy) pour pages lourdes ou moins fréquentes --
const Catalogue = lazy(() => import('@/pages/Catalogue'));
const Wishlists = lazy(() => import('@/pages/Wishlists'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Login = lazy(() => import('@/pages/Login'));
const RequestAccess = lazy(() => import('@/pages/RequestAccess'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminImageSearch = lazy(() => import('@/pages/AdminImageSearch'));
const OrderHistory = lazy(() => import('@/pages/Orders'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const DesignerProfile = lazy(() => import('@/pages/DesignerProfile'));
const ImageViewer = lazy(() => import('@/pages/ImageViewer'));
const FourHandsSearch = lazy(() => import('@/pages/FourHandsSearch'));
const QuoteEditor = lazy(() => import('@/pages/QuoteEditor'));
const WhiteGloveDelivery = lazy(() => import('@/pages/WhiteGloveDelivery'));
const TermsConditions = lazy(() => import('@/pages/TermsConditions'));
const ReturnPolicy = lazy(() => import('@/pages/ReturnPolicy'));
const MeasurementGuideline = lazy(() => import('@/pages/MeasurementGuideline'));
const PremiumFurnitureCare = lazy(() => import('@/pages/PremiumFurnitureCare'));
const RugCare = lazy(() => import('@/pages/RugCare'));
const StoneCare = lazy(() => import('@/pages/StoneCare'));
const OutdoorCare = lazy(() => import('@/pages/OutdoorCare'));
const LeatherCare = lazy(() => import('@/pages/LeatherCare'));
const UpholsteryCare = lazy(() => import('@/pages/UpholsteryCare'));
const WoodCare = lazy(() => import('@/pages/WoodCare'));
const DownCare = lazy(() => import('@/pages/DownCare'));
const OptionalProtectionPlan = lazy(() => import('@/pages/OptionalProtectionPlan'));

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* ScrollToTop: scrolls to top on route change (ensures footer links navigate to top) */}
      <ScrollToTop />
      <Helmet>
        <title>Design Vault - B2B Product Sourcing</title>
        <meta name="description" content="The B2B platform for interior designers and architects to source products, create project boards, and manage specifications." />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
  <Header />
  <IdleSessionManager timeoutMinutes={10} />
        <main className="flex-grow">
          <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalogue" element={
              <RequireDesignerOrAdmin>
                <Catalogue />
              </RequireDesignerOrAdmin>
            } />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/request-access" element={<RequestAccess />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <DesignerProfile />
              </ProtectedRoute>
            } />
            <Route path="/designer-profile" element={
              <ProtectedRoute>
                <DesignerProfile />
              </ProtectedRoute>
            } />
            <Route path="/image-viewer" element={<ImageViewer />} />
            <Route path="/admin" element={
              <RequireAdmin>
                <Admin />
              </RequireAdmin>
            } />
            <Route path="/admin/image-search" element={
              <RequireAdmin>
                <AdminImageSearch />
              </RequireAdmin>
            } />
            <Route 
              path="/wishlists" 
              element={
                <ProtectedRoute redirectUnauthenticatedTo="/">
                  <Wishlists />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/quotes/:id"
              element={
                <ProtectedRoute>
                  <QuoteEditor />
                </ProtectedRoute>
              }
            />
             <Route 
              path="/orders" 
              element={
                <ProtectedRoute>
                  <OrderHistory />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/fourhands"
              element={
                <ProtectedRoute>
                  <FourHandsSearch />
                </ProtectedRoute>
              }
            />
            <Route path="/white-glove-delivery" element={<WhiteGloveDelivery />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/measuring-for-delivery" element={<MeasurementGuideline />} />
            <Route path="/premium-furniture-care" element={<PremiumFurnitureCare />} />
            <Route path="/premium-furniture-care/rug" element={<RugCare />} />
            <Route path="/premium-furniture-care/stone" element={<StoneCare />} />
            <Route path="/premium-furniture-care/outdoor" element={<OutdoorCare />} />
            <Route path="/premium-furniture-care/leather" element={<LeatherCare />} />
            <Route path="/premium-furniture-care/upholstery" element={<UpholsteryCare />} />
            <Route path="/premium-furniture-care/wood" element={<WoodCare />} />
            <Route path="/premium-furniture-care/down" element={<DownCare />} />
            <Route path="/optional-protection-plan" element={<OptionalProtectionPlan />} />
          </Routes>
          </Suspense>
        </main>
        <Footer />
        <Toaster />
        <BottomActionButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          Back to top
        </BottomActionButton>
      </div>
    </Router>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    // If the url has a hash, try to scroll to the element with that id
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id) || document.querySelector(`a[name="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    // Otherwise scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname, hash]);
  return null;
}

export default App;